import * as React from 'react'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import { useTheme } from '@mui/material/styles'

import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import { UserContext } from '../UserContext'
import FilterChips from '../components/FilterChips'
import MenuItem from '../components/MenuItem'
import { generateMenuItems } from './TemplateMenuItems'
import useMediaQuery from '@mui/material/useMediaQuery'

import { sendToAnalytics } from '../Analytics'
import { useTrackSearch } from '../hooks/useTrackSearch'
import { isKeywordGraphApp } from '../appVariant'

export default function TemplateSection() {
    const { username } = React.useContext(UserContext)

    const [selectedCategory, setSelectedCategory] = React.useState('ideation')
    const [categoryBeforeSearch, setCategoryBeforeSearch] = React.useState(
        'ideation'
    )
    const [searchQuery, setSearchQuery] = React.useState('')

    const trackSearch = useTrackSearch('apps_page', username)

    const updateSelectedCategory = category => {
        setSelectedCategory(category)
        setCategoryBeforeSearch(category)
        sendToAnalytics({
            userId: username,
            event: 'choose_app_category',
            data: { category },
        })
    }

    const handleSearchChange = e => {
        const val = e.target.value
        setSearchQuery(val)
        if (val && selectedCategory !== 'all') {
            setSelectedCategory('all')
        } else if (
            !val &&
            selectedCategory === 'all' &&
            categoryBeforeSearch !== 'all'
        ) {
            setSelectedCategory(categoryBeforeSearch)
        }
        trackSearch(val)
    }

    const templateMenuItems = generateMenuItems(username)
    const categoryFiltered =
        selectedCategory === 'all'
            ? templateMenuItems
            : templateMenuItems.filter(({ category }) =>
                  category.includes(selectedCategory)
              )
    const filteredMenuItems = searchQuery
        ? categoryFiltered.filter(item => {
              const q = searchQuery.toLowerCase()
              return (
                  item.name.toLowerCase().includes(q) ||
                  (item.description &&
                      item.description.toLowerCase().includes(q))
              )
          })
        : categoryFiltered

    const isMobile = useMediaQuery('(max-width:600px)')

    return (
        <Box
            sx={{
                backgroundColor: theme => theme.palette.background.default,
                flexGrow: 1,
                ml: 4,
            }}
        >
            <Box
                sx={{
                    pt: 8,
                    pb: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 2 },
                }}
            >
                <Typography
                    component="h5"
                    variant="h5"
                    align="left"
                    color="text.primary"
                    sx={{ whiteSpace: { sm: 'nowrap' } }}
                >
                    {isKeywordGraphApp()
                        ? 'Explore KeywordGraph Applications'
                        : 'Explore InfraNodus Applications'}
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 220 }}
                />
            </Box>
            <Divider
                variant="middle"
                textAlign="left"
                sx={{ ml: 0, marginBottom: 2 }}
            />
            <Grid
                container
                spacing={isMobile ? 0 : 2}
                sx={{ ml: isMobile ? -4 : -2 }}
            >
                <Grid item md={4} xs={6}>
                    <FilterChips
                        onClick={updateSelectedCategory}
                        selectedCategory={selectedCategory}
                    />
                </Grid>
                <Grid item sx={{ mt: 6 }} md={8} xs={6}>
                    {renderTemplateMenu(filteredMenuItems)}
                </Grid>
            </Grid>
        </Box>
    )
}

function renderTemplateMenu(menuItems) {
    const theme = useTheme()

    return (
        <Container
            sx={{
                py: 1,
                [theme.breakpoints.up('sm')]: {
                    pl: 0,
                },
            }}
            maxWidth={false}
        >
            <Grid
                container
                rowSpacing={3}
                justifyContent="flex-start"
                columnSpacing={3}
                alignItems="flex-start"
                pr={0}
            >
                {menuItems.map(card => (
                    <Grid item key={card.name} alignItems="flex-start" pr={0}>
                        <MenuItem {...card} source="apps_grid" />
                    </Grid>
                ))}
            </Grid>
        </Container>
    )
}
